import { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'hu' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  hu: {
    // Header
    'nav.projects': 'REFERENCIÁK',
    'nav.about': 'PROFIL',
    'nav.contact': 'KAPCSOLAT',
    'nav.services': 'SZOLGÁLTATÁSOK',
    'nav.news': 'HÍREK',
    'nav.admin': 'ADMIN',
    
    // Projects
    'projects.title': 'Projektek',
    'projects.viewProject': 'Részletek',
    
    // Project Categories
    'category.residential': 'Lakóépület',
    'category.commercial': 'Kereskedelmi',
    'category.interior': 'Belső tér',
    'category.all': 'Összes',
    
    // Project Details
    'project.gallery': 'Galéria',
    'project.details': 'Részletek',
    'project.category': 'Kategória',
    'project.location': 'Helyszín',
    'project.year': 'Év',
    'project.area': 'Terület',
    'project.relatedProjects': 'Hasonló projektek',
    
    // About
    'about.title': 'Rólunk',
    'about.text1': 'A zsalako építészeti stúdió a kortárs design és a hagyományos kézművesség találkozásából jön létre. Több mint 15 éves tapasztalattal rendelkezünk a kiváló minőségű építészeti megoldások tervezésében.',
    'about.text2': 'Szakértő csapatunk a funkcionális és esztétikus terek létrehozására specializálódott, amelyek tükrözik ügyfeleink egyedi víziójit és igényeit.',
    
    // Services
    'services.title': 'Szolgáltatások',
    'services.architecture.title': 'Építészeti Tervezés',
    'services.architecture.desc': 'Átfogó építészeti megoldások a koncepciótól a kivitelezésig',
    'services.interior.title': 'Belsőépítészet',
    'services.interior.desc': 'Funkcionális és szép belső terek létrehozása',
    'services.renovation.title': 'Felújítás',
    'services.renovation.desc': 'Szakértő felújítási szolgáltatások minden típusú ingatlanhoz',
    
    // Contact
    'contact.title': 'Kapcsolat',
    'contact.info': 'Kapcsolati információk',
    'contact.office': 'iroda',
    'contact.address': 'Cím',
    'contact.phone': 'telefon',
    'contact.email': 'email',
    'contact.online': 'online',
    'contact.form.title': 'Send us a message',
    'contact.companyName': 'Cégnév',
    'contact.companyLocation': 'Telephely',
    'contact.taxNumber': 'Adószám',
    'contact.registrationNumber': 'Cégjegyzékszám',
    
    // Footer
    'footer.quickLinks': 'Gyors linkek',
    'footer.followUs': 'Kövess minket',
    'footer.rights': 'Minden jog fenntartva.',
    'footer.description': 'Kivételes építészeti élmények létrehozása innovatív tervezéssel és precíz kivitelezéssel.',
    
    // News
    'news.title': 'Hrek',
    'news.readMore': 'Tovább olvasom',
    'news.backToNews': 'Vissza a hírekhez',
    'news.published': 'Megjelent',
    'news.relatedNews': 'Kapcsolódó hírek',
    
    // Admin
    'admin.title': 'Cím',
    'admin.dashboard': 'Név',
    'admin.signOut': 'Kijelentkezés',
    'admin.logout': 'Kijelentkezés',
    'admin.projects': 'Projektek',
    'admin.news': 'Hírek',
    'admin.categories': 'Kategóriák',
    'admin.edit': 'Szerkesztés',
    'admin.delete': 'Törlés',
    'admin.add': 'Hozzáadás',
    'admin.addNew': 'Új hozzáadása',
    'admin.save': 'Mentés',
    'admin.cancel': 'Mégse',
    'admin.deleteConfirm': 'Biztos törölni szeretnéd?',
    'admin.uploadImages': 'Képek feltöltése',
    'admin.image': 'Kép',
    'admin.date': 'Dátum',
    'admin.category': 'Kategória',
    'admin.location': 'Helyszín',
    'admin.year': 'Év',
    
    // Login
    'login.title': 'Bejelentkezés',
    'login.description': 'Jelentkezz be az admin felülethez',
    'login.email': 'E-mail cím',
    'login.password': 'Jelszó',
    'login.submit': 'Bejelentkezés',
    'login.loggingIn': 'Bejelentkezés...',
    'login.error': 'Bejelentkezési hiba történt',
  },
  en: {
    // Header
    'nav.projects': 'REFERENCES',
    'nav.about': 'PROFILE',
    'nav.contact': 'CONTACT',
    'nav.services': 'SERVICES',
    'nav.news': 'NEWS',
    'nav.admin': 'ADMIN',
    
    // Projects
    'projects.title': 'Projects',
    'projects.viewProject': 'View Details',
    
    // Project Categories
    'category.residential': 'Residential',
    'category.commercial': 'Commercial',
    'category.interior': 'Interior',
    'category.all': 'All',
    
    // Project Details
    'project.gallery': 'Gallery',
    'project.details': 'Details',
    'project.category': 'Category',
    'project.location': 'Location',
    'project.year': 'Year',
    'project.area': 'Area',
    'project.relatedProjects': 'Related Projects',
    
    // About
    'about.title': 'About Us',
    'about.text1': 'zsalako architecture studio emerges from the intersection of contemporary design and traditional craftsmanship. With over 15 years of experience, we specialize in creating exceptional architectural solutions.',
    'about.text2': 'Our team of experts focuses on crafting functional and aesthetic spaces that reflect our clients\' unique vision and needs.',
    
    // Services
    'services.title': 'Services',
    'services.architecture.title': 'Architectural Design',
    'services.architecture.desc': 'Comprehensive architectural solutions from concept to execution',
    'services.interior.title': 'Interior Design',
    'services.interior.desc': 'Creating functional and beautiful interior spaces',
    'services.renovation.title': 'Renovation',
    'services.renovation.desc': 'Expert renovation services for all types of properties',
    
    // Contact
    'contact.title': 'Contact',
    'contact.info': 'Contact Information',
    'contact.office': 'office',
    'contact.address': 'Address',
    'contact.phone': 'phone',
    'contact.email': 'email',
    'contact.online': 'online',
    'contact.form.title': 'Send us a message',
    'contact.companyName': 'Company Name',
    'contact.companyLocation': 'Location',
    'contact.taxNumber': 'Tax Number',
    'contact.registrationNumber': 'Registration Number',
    
    // Footer
    'footer.quickLinks': 'Quick Links',
    'footer.followUs': 'Follow Us',
    'footer.rights': 'All rights reserved.',
    'footer.description': 'Creating exceptional architectural experiences through innovative design and precise execution.',
    
    // News
    'news.title': 'News',
    'news.readMore': 'Read More',
    'news.backToNews': 'Back to News',
    'news.published': 'Published',
    'news.relatedNews': 'Related News',
    
    // Admin
    'admin.title': 'Title',
    'admin.dashboard': 'Name',
    'admin.signOut': 'Sign Out',
    'admin.logout': 'Logout',
    'admin.projects': 'Projects',
    'admin.news': 'News',
    'admin.categories': 'Categories',
    'admin.edit': 'Edit',
    'admin.delete': 'Delete',
    'admin.add': 'Add',
    'admin.addNew': 'Add New',
    'admin.save': 'Save',
    'admin.cancel': 'Cancel',
    'admin.deleteConfirm': 'Are you sure you want to delete?',
    'admin.uploadImages': 'Upload Images',
    'admin.image': 'Image',
    'admin.date': 'Date',
    'admin.category': 'Category',
    'admin.location': 'Location',
    'admin.year': 'Year',
    
    // Login
    'login.title': 'Login',
    'login.description': 'Sign in to access the admin dashboard',
    'login.email': 'Email address',
    'login.password': 'Password',
    'login.submit': 'Sign In',
    'login.loggingIn': 'Signing in...',
    'login.error': 'Login error occurred',
  },
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('hu');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.hu] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}